<script setup>
import { usePhonePaste } from '@/composables/phonePaste'
import { usePhonebookStore } from '@/stores/phonebook.store'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useNotify } from '@/composables/notify'
import { useDetectDevice } from '@/composables/device'
import i18n from '@/plugins/i18n'
import * as usersApi from '@/apis/auth.api'

// consente di incollare il numero: la maschera del campo tel scarta tutto cio che non e cifra
const { onPhonePaste: onContactPhonePaste } = usePhonePaste((v) => { form.phone_number = v })

const router = useRouter()
const route = useRoute()
const { notifyError, notifySuccess } = useNotify()
const { isMobile } = useDetectDevice()
const t = i18n.global.t
const locale = i18n.global.locale
const phonebookStore = usePhonebookStore()

const props = defineProps({
  itemId: { type: [String, Number], required: false },
  fullScreen: { type: Boolean, default: false },
  title: { type: String, default: 'phonebook.create.title' },
  mode: { type: String, required: true, validator: value => ['edit', 'create'].includes(value) },
  handleClose: { type: Boolean, required: false, default: true }
})

const emit = defineEmits(['update:modelValue', 'saved', 'closed'])
const loading = ref(true)
const contactForm = ref(null)

const form = reactive({
  name: '',
  phone_number: ''
})

const initialData = reactive({})
const show = computed({
  get: () => props.mode === 'edit' ? !!route.query.edit : !!route.query.create,
  set: value => { if (!value) closeDialog() }
})

const hasChanges = computed(() => {
  if (props.mode === 'create') return true
  return Object.keys(initialData).some(key => form[key] !== initialData[key])
})

const resetForm = (data = {}) => {
  Object.assign(form, {
    name: '',
    phone_number: '',
    ...data
  })
}

watch(show, (isOpen) => {
  if (!isOpen) {
    resetForm()
  }
})

const fetchContactDetailAsync = async() => {
  if (props.mode !== 'edit' || !props.itemId) return

  try {
    // Check if contact is already in store
    let contact = phonebookStore.allContacts.find(c => c.id === Number(props.itemId))

    if (!contact) {
      const { data } = await usersApi.getContactDetailAsync(props.itemId)
      contact = data
    }

    if (contact) {
      resetForm(contact)
      Object.assign(initialData, contact)
    }
  } catch (error) {
    notifyError(t('phonebook.notifications.errorFetchingDetails'))
  }
}

const save = async() => {
  loading.value = true

  const isValid = await contactForm.value.validate()
  if (!isValid) {
    notifyError(t('form.notifications.fixErrors'))
    loading.value = false
    return
  }
  const payload = {
    phone_number: form.phone_number,
    name: form.name,
  }
  try {
    if (props.mode === 'edit') {
      await usersApi.updateContactAsync(props.itemId, payload)
    } else {
      await usersApi.createContactAsync(payload)
    }
    notifySuccess(t('phonebook.notifications.savingSuccess'))
    closeDialog()
  } catch (error) {
    if (error.response?.status === 500) {
      notifyError(t('error.serverError'))
      return
    } else {
      notifyError(t(error?.response?.data[0]))
    }
  } finally {
    emit('saved')
    resetForm()
    loading.value = false
  }
}

const closeDialog = () => {
  emit('closed')
  resetForm()
  if (!props.handleClose) return
  const query = { ...route.query }
  if (props.mode === 'edit') delete query.edit
  if (props.mode === 'create') delete query.create
  router.replace({ query })
}

onMounted(async() => {
  if (props.mode === 'edit' && !props.itemId) {
    loading.value = false
    return
  }

  await Promise.all([fetchContactDetailAsync()])

  loading.value = false
})

watch(() => props.itemId, fetchContactDetailAsync)

</script>

<template>
  <q-dialog
    v-model="show"
    :maximized="fullScreen || isMobile"
  >
    <q-card
      v-touch-swipe.mouse.right="closeDialog"
      class="dialog-card"
    >
      <!-- Header -->
      <q-card-section class="dialog-header bg-primary text-white row items-center">
        <div class="col">
          <div class="text-h6">
            {{ $t(props.title) }}
          </div>
        </div>
      </q-card-section>

      <q-separator />

      <!-- Body -->
      <div class="dialog-body q-pa-md">
        <q-form
          ref="contactForm"
          class="q-gutter-md"
          @submit.prevent="save"
        >
          <!-- Name-->
          <q-input
            v-model="form.name"
            :label="$t('phonebook.create.name')"
            :rules="[val => !!val || $t('form.field.required')]"
            required
          />
          <!-- Phone Number -->

          <q-tel-input
            v-model="form.phone_number"
            @paste.capture="onContactPhonePaste"
            :locales="locale"
            :label="$t('phonebook.create.phoneNumber')"
          />
        </q-form>
      </div>

      <!-- Footer -->
      <q-card-actions class="dialog-footer q-gutter-sm bg-white">
        <q-btn
          :label="$t('common.cancel')"
          flat
          :loading="loading"
          @click="closeDialog"
        />
        <q-btn
          :label="$t('common.save')"
          color="primary"
          :loading="loading"
          :disabled="!hasChanges || loading"
          @click="save"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
